import type * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive: "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
        // Success states
        success: "border-transparent bg-green-600 text-white hover:bg-green-700",
        // Error states
        error: "border-transparent bg-red-600 text-white hover:bg-red-700",
        // Warning states
        warning: "border-transparent bg-yellow-600 text-white hover:bg-yellow-700",
        // Info states
        info: "border-transparent bg-blue-600 text-white hover:bg-blue-700",
        // Action-specific variants
        added: "border-transparent bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm",
        modified: "border-transparent bg-amber-600 text-white hover:bg-amber-700 shadow-sm",
        deleted: "border-transparent bg-red-600 text-white hover:bg-red-700 shadow-sm",
        // Percentage validation variants
        "percentage-valid": "border-transparent bg-green-600 text-white hover:bg-green-700 font-bold",
        "percentage-error": "border-transparent bg-red-600 text-white hover:bg-red-700 font-bold animate-pulse",
        "percentage-warning": "border-transparent bg-orange-600 text-white hover:bg-orange-700 font-bold",
        // Z-blue variant for branding
        zblue:
          "border-transparent bg-[var(--color-z-blue)]/20 text-[var(--color-z-blue)] hover:bg-[var(--color-z-blue)]/30",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />
}

export { Badge, badgeVariants }
